import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../api/client';
import { CameraDevice, CustomerFleetSummary, AlertItem, Customer, OfflineTriageList } from '../../types';
import { MetricCard } from '../../components/common/MetricCard';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  Users,
  Radio,
  ArrowRight,
  ShieldAlert,
  GitPullRequest,
  Activity,
  CreditCard,
  KeyRound,
  Loader2,
  Phone,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

export const OverviewPage: React.FC = () => {
  const { role } = useAuth();
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [fleets, setFleets] = useState<CustomerFleetSummary[]>([]);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [summary, setSummary] = useState<{
    totalCustomers: number;
    activePlans: number;
    mrr: number;
    currency: string;
    byStatus: Record<string, number>;
  } | null>(null);
  const [fleetTotals, setFleetTotals] = useState<{
    cameras: number;
    online: number;
    offline: number;
    sitesDown: number;
    customersWithCameras: number;
    inactivated: number;
    neverReported: number;
    silent: number;
    silentReachable: number;
    silentDead: number;
  } | null>(null);
  const [triage, setTriage] = useState<OfflineTriageList | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [job, setJob] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Poll a running async job (re-probe / backfill) to completion, then reload.
  const pollJob = (which: 'presence' | 'backfill', label: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = which === 'presence' ? await api.getPresenceStatus() : await api.getBackfillStatus();
        if (s && s.running === false) {
          if (pollRef.current) clearInterval(pollRef.current);
          setJob(null);
          setFeedback(
            which === 'presence'
              ? `Re-probe done: ${s.active ?? 0} reachable, ${s.inactivated ?? 0} dead.`
              : `Resolve done: ${s.resolved ?? 0} resolved, ${s.updated ?? 0} written.`,
          );
          loadData();
        } else if (s && s.processed != null && s.scanned != null) {
          setJob(`${label} — ${s.processed}/${s.scanned}…`);
        }
      } catch { /* transient; keep polling */ }
    }, 4000);
  };

  const runRefresh = async () => {
    setFeedback(null);
    try {
      const r = await api.refreshPresence();
      if (!r.started) { setFeedback('A presence refresh is already running.'); return; }
      setJob(`Re-probing eye4 for ${r.toProcess} cameras…`);
      pollJob('presence', 'Re-probing');
    } catch (e: any) { setFeedback(e?.message || 'Failed to start presence refresh'); }
  };

  const runBackfill = async () => {
    setFeedback(null);
    try {
      const r = await api.backfillVuids(false);
      if (!r.started) { setFeedback('A UID backfill is already running.'); return; }
      setJob(`Resolving UIDs for ${r.toProcess} cameras…`);
      pollJob('backfill', 'Resolving UIDs');
    } catch (e: any) { setFeedback(e?.message || 'Failed to start UID backfill'); }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Each call is independent — one failure must not blank the whole page.
      const settle = async <T,>(p: Promise<T>, fallback: T, label: string): Promise<T> => {
        try {
          return await p;
        } catch (e) {
          console.warn(`[overview] ${label} failed:`, e);
          return fallback;
        }
      };
      const [devList, fleetList, alertList, custList, summaryData, totals, triageData] = await Promise.all([
        settle(api.getDevices(), [] as CameraDevice[], 'devices'),
        settle(api.getCustomerFleets(), [] as CustomerFleetSummary[], 'fleets'),
        settle(api.getAlerts(), [] as AlertItem[], 'alerts'),
        settle(api.getCustomers(), [] as Customer[], 'customers'),
        settle(api.getCustomerSummary(), null, 'summary'),
        settle(api.getFleetTotals(), null, 'fleet-totals'),
        settle(api.getOfflineTriage('needs_reactivation', 1, 5), null, 'triage'),
      ]);
      setDevices(devList);
      setFleets(fleetList);
      setAlerts(alertList);
      setCustomers(custList);
      setSummary(summaryData);
      setFleetTotals(totals);
      setTriage(triageData);
    } catch (err) {
      console.error('Failed to load overview data', err);
    } finally {
      setLoading(false);
    }
  };

  const totalCameras = fleetTotals?.cameras ?? devices.length;
  const onlineCameras = fleetTotals?.online ?? devices.filter((d) => d.status === 'online').length;
  const offlineCameras = fleetTotals?.offline ?? devices.filter((d) => d.status === 'offline').length;
  const degradedCameras = devices.filter((d) => d.status === 'degraded').length; // totals has no degraded bucket
  const sitesFullyDown = fleetTotals?.sitesDown ?? fleets.filter((f) => f.isSiteFullyDown).length;

  const fleetStatusData = [
    { name: 'Online', value: onlineCameras, color: '#22c55e' },
    { name: 'Offline', value: offlineCameras, color: '#ef4444' },
    { name: 'Degraded', value: degradedCameras, color: '#f59e0b' },
  ].filter((d) => d.value > 0);

  const subStatusColors: Record<string, string> = {
    active: '#22c55e',
    trialing: '#38bdf8',
    pending: '#f59e0b',
    past_due: '#f97316',
    canceled: '#ef4444',
    inactive: '#64748b',
    none: '#475569',
  };
  const subStatusData = summary
    ? Object.entries(summary.byStatus)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => ({ name: k.replace(/_/g, ' '), value: v, color: subStatusColors[k] || '#64748b' }))
    : [];

  return (
    <div>
      {/* Top Notification / Incident Banner if cameras are down */}
      {offlineCameras > 0 && (
        <div
          style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-lg)',
            padding: '14px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={20} color="var(--danger-text)" />
            <div>
              <strong style={{ color: 'var(--danger-text)' }}>
                {offlineCameras} camera{offlineCameras === 1 ? '' : 's'} not reporting
              </strong>
              {sitesFullyDown > 0 ? ` — ${sitesFullyDown} site${sitesFullyDown === 1 ? '' : 's'} with none online.` : '.'}
              {fleetTotals && (() => {
                // Honest split: dead (needs reactivation) vs quiet (false-offline).
                const dead = fleetTotals.inactivated + fleetTotals.silentDead;
                const quiet = fleetTotals.silentReachable;
                return (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    <strong style={{ color: 'var(--danger-text)' }}>{dead.toLocaleString()}</strong> need reactivation
                    {' · '}<strong style={{ color: 'var(--success-text)' }}>{quiet.toLocaleString()}</strong> alive but quiet (not real outages)
                    {' · '}{fleetTotals.neverReported.toLocaleString()} never reported
                    {fleetTotals.silent > fleetTotals.silentReachable + fleetTotals.silentDead
                      ? ` · ${(fleetTotals.silent - fleetTotals.silentReachable - fleetTotals.silentDead).toLocaleString()} unprobed`
                      : ''}
                  </div>
                );
              })()}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {job ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--info-text)' }}>
                <Loader2 size={13} className="spin" /> {job}
              </span>
            ) : (
              <>
                <button className="btn btn-secondary btn-sm" onClick={runRefresh} title="Re-probe eye4 for the quiet cameras to split dead from just-idle">
                  <Radio size={13} /> <span>Re-probe</span>
                </button>
                {fleetTotals && fleetTotals.inactivated > 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={runBackfill} title="Resolve empty eye4 UIDs">
                    <KeyRound size={13} /> <span>Resolve UIDs</span>
                  </button>
                )}
              </>
            )}
            <Link to="/offline-triage" className="btn btn-danger btn-sm">
              <span>Open Triage Board</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}
      {feedback && (
        <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-border)', color: 'var(--info-text)', padding: '9px 14px', borderRadius: 'var(--radius-md)', marginTop: '-14px', marginBottom: '24px', fontSize: 12.5 }}>
          {feedback}
        </div>
      )}

      {/* KPI Metric Cards Grid */}
      <div className="kpi-grid">
        <MetricCard
          label="Fleet Online Health"
          value={`${onlineCameras} / ${totalCameras}`}
          source="GET /v2/admin/devices"
          icon={<CheckCircle2 size={18} />}
          trend={`${Math.round((onlineCameras / (totalCameras || 1)) * 100)}% online rate`}
          subtext={`${offlineCameras} offline • ${degradedCameras} degraded`}
        />

        <MetricCard
          label="Fully Down Sites"
          value={sitesFullyDown}
          source="GET /v2/admin/customer-fleets"
          icon={<AlertTriangle size={18} />}
          subtext="Customer locations with 0% camera online"
        />

        {/* Finance-focused or Admin-focused Metric */}
        {['super_admin', 'admin', 'finance'].includes(role) ? (
          <MetricCard
            label="Monthly Recurring Revenue"
            value={summary ? `$${summary.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
            icon={<DollarSign size={18} />}
            subtext={summary ? `${summary.activePlans} paying account${summary.activePlans === 1 ? '' : 's'}` : undefined}
          />
        ) : (
          <MetricCard
            label="Cameras Offline"
            value={offlineCameras}
            icon={<Radio size={18} />}
            subtext={`${degradedCameras} degraded`}
          />
        )}

        <MetricCard
          label="Customer Accounts"
          value={summary ? summary.totalCustomers.toLocaleString() : customers.length}
          icon={<Users size={18} />}
          subtext={summary ? `${summary.activePlans} on an active plan` : undefined}
        />
      </div>

      {/* Insights: fleet status + subscription mix (real) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={16} color="var(--primary-light)" />
                <span>Fleet Status</span>
              </div>
              <div className="card-subtitle">{totalCameras.toLocaleString()} cameras platform-wide</div>
            </div>
          </div>
          <div style={{ height: 230 }}>
            {fleetStatusData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={fleetStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={88} paddingAngle={2}>
                    {fleetStatusData.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid var(--border-dim)', borderRadius: 8, fontSize: 12, color: '#e2e8f0' }}
                    itemStyle={{ color: '#e2e8f0' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No camera data</div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CreditCard size={16} color="var(--primary-light)" />
                <span>Subscriptions</span>
              </div>
              <div className="card-subtitle">
                {summary ? `${summary.activePlans} active of ${summary.totalCustomers.toLocaleString()} accounts` : 'Loading…'}
              </div>
            </div>
          </div>
          <div style={{ height: 230 }}>
            {subStatusData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={subStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={58} outerRadius={88} paddingAngle={2}>
                    {subStatusData.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid var(--border-dim)', borderRadius: 8, fontSize: 12, color: '#e2e8f0' }}
                    itemStyle={{ color: '#e2e8f0' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No subscription data</div>
            )}
          </div>
        </div>
      </div>

      {/* Two Column Layout: Down Cameras Triage + Latest Alerts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {/* Urgent Action / Down Cameras */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GitPullRequest size={16} color="var(--danger-text)" />
                <span>Needs Reactivation</span>
              </div>
              <div className="card-subtitle">
                {triage ? `${triage.counts.needsReactivation.toLocaleString()} dead on eye4 — call the owner to power-cycle / reactivate` : 'Offline cameras that eye4 confirms are dead'}
              </div>
            </div>
            <Link to="/offline-triage" className="btn btn-ghost btn-sm">
              View All <ArrowRight size={13} />
            </Link>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {(triage?.cameras ?? []).map((cam) => {
              const phone = cam.ownerPhone || cam.namePhone;
              return (
                <div
                  key={cam.id}
                  style={{
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      <strong style={{ fontSize: '13px' }}>{cam.name || '(unnamed)'}</strong>
                      <span style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', borderRadius: 999, padding: '1px 8px', fontSize: 10.5, fontWeight: 600 }}>Dead</span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                      {cam.ownerName || cam.ownerEmail} • <code style={{ color: 'var(--primary-light)' }}>{cam.serialNumber}</code>
                    </div>
                  </div>
                  {phone ? (
                    <a href={`tel:${phone}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      <Phone size={13} /> <span>{phone}</span>
                    </a>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>No number</span>
                  )}
                </div>
              );
            })}
            {triage && triage.counts.needsReactivation === 0 && (
              <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12.5 }}>
                No cameras confirmed dead.
              </div>
            )}
          </div>
        </div>

        {/* Latest Alerts Stream */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldAlert size={16} color="var(--warning-text)" />
                <span>Live Security & System Alerts</span>
              </div>
              <div className="card-subtitle">Real-time alerts (Footage firewall active)</div>
            </div>
            <Link to="/alerts" className="btn btn-ghost btn-sm">
              All Alerts <ArrowRight size={13} />
            </Link>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {alerts.slice(0, 4).map((alert) => (
              <div
                key={alert.id}
                style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <StatusBadge status={alert.severity} />
                    <span style={{ fontSize: '12.5px', fontWeight: 600 }}>
                      {alert.deviceName || alert.deviceSerial || 'Camera'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-main)', marginTop: '2px' }}>
                    {alert.message}
                  </div>
                  <div style={{ fontSize: '10.5px', color: 'var(--text-dim)', marginTop: '4px' }}>
                    {alert.timestamp ? new Date(alert.timestamp).toLocaleString() : ''}
                  </div>
                </div>
                <StatusBadge status={alert.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};
